import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { AuthContext } from './AuthContext';
import './UserRequest.css'; // CSS-Datei für Styles und Progress-Bar
import { translate } from './translateFunction'; // Import the translate function
import { FaFilePdf, FaRegFileWord } from 'react-icons/fa'; // Import PDF and Word icons


const FeedbackSelectionExport = () => {
    const { user } = useContext(AuthContext);
    const [feedbacks, setFeedbacks] = useState([]);
    const [selectedFeedbacks, setSelectedFeedbacks] = useState([]);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCV, setSelectedCV] = useState(null);
    const [exportType, setExportType] = useState('feedback-only'); // 'feedback-only' oder 'cv-combined'
    const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' oder 'docx'

    // Reset CV when format changes to ensure compatibility
    useEffect(() => {
        if (exportType === 'cv-combined') {
            // Bei CV-Combined immer PDF verwenden
            setExportFormat('pdf');
            // Reset CV nur wenn es nicht PDF-kompatibel ist
            if (selectedCV) {
                const isPdfFile = selectedCV.type === 'application/pdf';
                if (!isPdfFile) {
                    setSelectedCV(null);
                }
            }
        }
    }, [exportType, selectedCV]);

    // Helper function to generate exactly 5 stars with colors
    const generateStars = (rating) => {
        const validRating = Math.max(0, Math.min(5, Math.floor(rating || 0)));
        const stars = [];
        
        // Gefüllte gelbe Sterne
        for (let i = 0; i < validRating; i++) {
            stars.push(
                <span key={`filled-${i}`} style={{ color: '#FFD700' }}>★</span>
            );
        }
        
        // Leere schwarze/graue Sterne
        for (let i = validRating; i < 5; i++) {
            stars.push(
                <span key={`empty-${i}`} style={{ color: '#666666' }}>☆</span>
            );
        }
        
        return stars;
    };

    // State for translations
    const [translations, setTranslations] = useState({
        receivedFeedbackTitle: '',
        receivedOn: '',
        customer: '',
        contactPerson: '',
        email: '',
        rating: '',
        actions: '',
        noFeedbacksFound: '',
        viewButton: '',
        closeButton: '',
        feedbackLabel: '',
        select: '',
        subtitel: '',
        toptitel: '',
        exportOptions: '',
        feedbackOnlyExport: '',
        cvFeedbackCombined: '',
        cvUpload: '',
        pdfOnly: '',
        exportSelection: '',
        selected: '',
        exporting: '',
        exportCvFeedback: '',
        selectPdfFile: '',
        selectCvFirst: '',
        exportFormat: '',
        pdfFormat: '',
        docxFormat: '',
        exportWordFeedback: ''
    });

    // Load translations when the component mounts
    useEffect(() => {
        const loadTranslations = async () => {
            const receivedFeedbackTitle = await translate(439); // "Erhaltene Feedbacks"
            const receivedOn = await translate(231); // "Erhalten am"
            const customer = await translate(232); // "Kunde"
            const contactPerson = await translate(233); // "Ansprechperson"
            const email = await translate(234); // "E-Mail"
            const rating = await translate(235); // "Bewertung"
            const actions = await translate(236); // "Aktionen"
            const noFeedbacksFound = await translate(237); // "Keine Feedbacks gefunden"
            const viewButton = await translate(238); // "Anzeigen"
            const closeButton = await translate(435); // "Export Selection"
            const feedbackLabel = await translate(240); // "Feedback"
            const select = await translate(241); // "Auswählen"
            const subtitel = await translate(438); // "Untertitel"
            const toptitel = await translate(437); // "Titel"
            const exportOptions = await translate(900); // "Export-Optionen"
            const feedbackOnlyExport = await translate(901); // "Nur Kundenfeedback exportieren"
            const cvFeedbackCombined = await translate(902); // "CV + Kundenfeedback kombinieren"
            const cvUpload = await translate(903); // "CV hochladen"
            const pdfOnly = await translate(904); // "PDF nur"
            const exportSelection = await translate(915); // "PDF-Export"
            const selected = await translate(905); // "ausgewählt"
            const exporting = await translate(906); // "Exportieren..."
            const exportCvFeedback = await translate(907); // "CV + Feedback exportieren"
            const selectPdfFile = await translate(908); // "Bitte wählen Sie eine PDF-Datei aus."
            const selectCvFirst = await translate(909); // "Bitte wählen Sie erst Ihr CV aus."
            const exportFormat = await translate(910); // "Export-Format"
            const pdfFormat = await translate(911); // "PDF-Format"
            const docxFormat = await translate(912); // "Word (DOCX)"
            const exportWordFeedback = await translate(914); // "Word-Export"

            setTranslations({
                receivedFeedbackTitle,
                receivedOn,
                customer,
                contactPerson,
                email,
                rating,
                actions,
                noFeedbacksFound,
                viewButton,
                closeButton,
                feedbackLabel,
                select,
                subtitel,
                toptitel,
                exportOptions,
                feedbackOnlyExport,
                cvFeedbackCombined,
                cvUpload,
                pdfOnly,
                exportSelection,
                selected,
                exporting,
                exportCvFeedback,
                selectPdfFile,
                selectCvFirst,
                exportFormat,
                pdfFormat,
                docxFormat,
                exportWordFeedback
            });
        };

        loadTranslations();
    }, []);

    useEffect(() => {
        const fetchFeedbacks = async () => {
            const token = localStorage.getItem('token');
            if (token && user && user.id) {
                try {
                    const response = await axios.get(
                        `/api/customerFeedback/userReceived/${user.id}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setFeedbacks(response.data || []);
                } catch (error) {
                    console.error('Error fetching feedbacks:', error);
                }
            }
        };

        fetchFeedbacks();
    }, [user]);

    const handleSelection = (feedbackId) => {
        setSelectedFeedbacks((prev) =>
            prev.includes(feedbackId)
                ? prev.filter((id) => id !== feedbackId)
                : [...prev, feedbackId]
        );
    };

    const handleCVUpload = (event) => {
        const file = event.target.files[0];
        
        // Für CV-Combined nur PDF erlauben
        if (file && file.type === 'application/pdf') {
            setSelectedCV(file);
        } else {
            alert(translations.selectPdfFile);
        }
    };

    const handleExport = async () => {
        setShowProgressBar(true);
        setIsSubmitting(true);

        try {
            const selectedData = feedbacks.filter((feedback) =>
                selectedFeedbacks.includes(feedback.customerFdbckID)
            );

            if (exportType === 'cv-combined' && !selectedCV) {
                alert(translations.selectCvFirst);
                setShowProgressBar(false);
                setIsSubmitting(false);
                return;
            }

            let response;
            if (exportType === 'cv-combined' && selectedCV) {
                // Combined CV + Feedback export (immer PDF)
                const formData = new FormData();
                formData.append('cv', selectedCV);
                formData.append('feedbacks', JSON.stringify(selectedData));

                response = await axios.post(
                    '/api/export-cv-feedback',
                    formData,
                    { 
                        responseType: 'blob',
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            } else {
                // Regular feedback-only export (PDF oder DOCX)
                const endpoint = exportFormat === 'docx' ? '/api/export-feedbacks-docx' : '/api/export-feedbacks';
                response = await axios.post(
                    endpoint,
                    { feedbacks: selectedData },
                    { responseType: 'blob' }
                );
            }

            setTimeout(() => {
                const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                let fileName;
                let mimeType;
                
                if (exportType === 'cv-combined') {
                    // CV-Combined ist immer PDF
                    fileName = `${currentDate}_${user.name}_CV_mit_Feedback.pdf`;
                    mimeType = 'application/pdf';
                } else {
                    // Feedback-only kann PDF oder DOCX sein
                    const fileExtension = exportFormat;
                    fileName = `${currentDate}_${user.name}_exportFeedback.${fileExtension}`;
                    mimeType = fileExtension === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
                }

                const blob = new Blob([response.data], { type: mimeType });
                saveAs(blob, fileName);

                setShowProgressBar(false);
                setIsSubmitting(false);
            }, 3000); // Match progress bar duration
        } catch (error) {
            console.error('Error exporting feedbacks:', error);
            setShowProgressBar(false);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-fluid mt-5">
            {showProgressBar && <div className="progress-bar"></div>}
            <section className="featured">
                <div className="content-wrapper banner">
                    <div className="float-right">
                        <img className="page-icon" src="/Content/themes/base/images/Speak.png" alt="Speak icon" />
                    </div>
                    <hgroup className="title">
                        <h1>{translations.toptitel}</h1>
                        <p>{translations.subtitel}</p>
                    </hgroup>
                </div>
            </section>
            <div className="container mt-5">
                <h3>{translations.receivedFeedbackTitle}</h3>
                <table className="table">
                    <thead>
                        <tr>
                            <th>{translations.select}</th>
                            <th>{translations.receivedOn}</th>
                            <th>{translations.customer}</th>
                            <th>{translations.contactPerson}</th>
                            <th>{translations.email}</th>
                            <th>{translations.rating}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feedbacks.length > 0 ? (
                            feedbacks.map((feedback) => (
                                <tr key={feedback.customerFdbckID}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedFeedbacks.includes(feedback.customerFdbckID)}
                                            onChange={() => handleSelection(feedback.customerFdbckID)}
                                        />
                                    </td>
                                    <td>{new Date(feedback.customerFdbckReceived).toLocaleDateString("fr-ch")}</td>
                                    <td>{feedback.customerCompany}</td>
                                    <td>{feedback.customerName}</td>
                                    <td>{feedback.customerMailaddr}</td>
                                    <td>{generateStars(feedback.rating)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6">No feedbacks available.</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Export-Typ Auswahl */}
                <div className="mb-4 mt-4">
                    <h3>{translations.exportOptions}</h3>
                    <div className="form-check" style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                        <input
                            className="form-check-input"
                            type="radio"
                            name="exportType"
                            id="feedback-only"
                            value="feedback-only"
                            checked={exportType === 'feedback-only'}
                            onChange={(e) => setExportType(e.target.value)}
                            style={{marginRight: '8px'}}
                        />
                        <p style={{fontWeight: 'normal', margin: '0'}}>
                            {translations.feedbackOnlyExport}
                        </p>
                    </div>
                    <div className="form-check" style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                        <input
                            className="form-check-input"
                            type="radio"
                            name="exportType"
                            id="cv-combined"
                            value="cv-combined"
                            checked={exportType === 'cv-combined'}
                            onChange={(e) => setExportType(e.target.value)}
                            style={{marginRight: '8px'}}
                        />
                        <p style={{fontWeight: 'normal', margin: '0'}}>
                            {translations.cvFeedbackCombined}
                        </p>
                    </div>
                </div>

                {/* Format-Auswahl (nur für Feedback-only) */}
                {exportType === 'feedback-only' && (
                    <div className="mb-4">
                        <h3>{translations.exportFormat}</h3>
                        <div className="export-format-options" style={{display: 'flex', gap: '20px', marginTop: '15px'}}>
                            <label className={`export-format-option ${exportFormat === 'pdf' ? 'selected' : ''}`} 
                                   style={{
                                       display: 'flex', 
                                       alignItems: 'center', 
                                       padding: '15px', 
                                       border: exportFormat === 'pdf' ? '2px solid #007bff' : '2px solid #ddd',
                                       borderRadius: '8px',
                                       cursor: 'pointer',
                                       backgroundColor: exportFormat === 'pdf' ? '#f0f8ff' : '#fff',
                                       transition: 'all 0.3s ease',
                                       minWidth: '120px'
                                   }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="pdf"
                                    checked={exportFormat === 'pdf'}
                                    onChange={(e) => setExportFormat(e.target.value)}
                                    style={{ display: 'none' }}
                                />
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
                                    <FaFilePdf 
                                        style={{
                                            fontSize: '32px', 
                                            color: exportFormat === 'pdf' ? '#dc3545' : '#6c757d'
                                        }} 
                                    />
                                    <span style={{fontWeight: exportFormat === 'pdf' ? 'bold' : 'normal'}}>
                                        PDF
                                    </span>
                                </div>
                            </label>
                            <label className={`export-format-option ${exportFormat === 'docx' ? 'selected' : ''}`}
                                   style={{
                                       display: 'flex', 
                                       alignItems: 'center', 
                                       padding: '15px', 
                                       border: exportFormat === 'docx' ? '2px solid #007bff' : '2px solid #ddd',
                                       borderRadius: '8px',
                                       cursor: 'pointer',
                                       backgroundColor: exportFormat === 'docx' ? '#f0f8ff' : '#fff',
                                       transition: 'all 0.3s ease',
                                       minWidth: '120px'
                                   }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="docx"
                                    checked={exportFormat === 'docx'}
                                    onChange={(e) => setExportFormat(e.target.value)}
                                    style={{ display: 'none' }}
                                />
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
                                    <FaRegFileWord 
                                        style={{
                                            fontSize: '32px', 
                                            color: exportFormat === 'docx' ? '#2b579a' : '#6c757d'
                                        }} 
                                    />
                                    <span style={{fontWeight: exportFormat === 'docx' ? 'bold' : 'normal'}}>
                                        DOCX
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>
                )}

                {/* CV Upload (nur sichtbar wenn kombiniert gewählt) */}
                {exportType === 'cv-combined' && (
                    <div className="form-group">
                        <label>{translations.cvUpload}</label>
                        <input 
                            type="file" 
                            className="form-control" 
                            accept=".pdf"
                            onChange={handleCVUpload} 
                        />
                        {selectedCV && (
                            <small className="text-success">
                                ✓ {selectedCV.name} {translations.selected}
                            </small>
                        )}
                    </div>
                )}

                <button
                    className="btn btn-primary button"
                    onClick={handleExport}
                    disabled={selectedFeedbacks.length === 0 || isSubmitting}
                >
                    {isSubmitting ? translations.exporting : 
                     exportType === 'cv-combined' ? translations.exportCvFeedback : 
                     exportFormat === 'docx' ? translations.exportWordFeedback : translations.exportSelection}
                </button>
            </div>
        </div>
    );
};

export default FeedbackSelectionExport;
